/**
 * Enhanced Table — ui-ux-pro-max + MASTER Density 8/10
 * Dense cells, sticky headers, calm borders, tabular nums,
 * empty/error states with focusable next actions.
 */

'use client';

import React, { useState, useEffect, useMemo } from 'react';
import { Table, TableProps, Button, Dropdown, Space, Tooltip, Input, Select } from 'antd';
import {
  SettingOutlined,
  FilterOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
  ReloadOutlined,
  DownloadOutlined,
  SearchOutlined,
  InboxOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useTheme } from '@/shared/theme';
import {
  useTableService,
  useMergedConfig,
  TableConfig,
  TableFeatures,
} from '@/shared/utils/tables/table.service';
import { FeedbackState } from '@/shared/components/feedback/FeedbackState';
import styles from './tables.module.css';

export type TableDensity = 'dense' | 'comfortable';

interface EnhancedTableProps<T = unknown> extends Omit<TableProps<T>, 'className'> {
  /** Additional CSS classes */
  className?: string;
  /** Table configuration ID for service-based configuration */
  tableId?: string;
  /** Whether to show enhanced styling (default: true) */
  enhanced?: boolean;
  /** Custom row background color (legacy — prefer CSS module zebra) */
  rowBackgroundColor?: string;
  /** Custom header background color (legacy) */
  headerBackgroundColor?: string;
  /** Whether to show zebra striping */
  zebraStriped?: boolean;
  /** Custom hover color (legacy) */
  hoverColor?: string;
  /** Whether to show the toolbar with settings (default: true) */
  showToolbar?: boolean;
  /** Callback when refresh is clicked */
  onRefresh?: () => void;
  /** Callback when export is clicked */
  onExport?: () => void;
  /** Callback when filter is applied */
  onFilter?: (filters: unknown) => void;
  /** Callback when sort is applied */
  onSort?: (sorter: unknown) => void;
  /** Override service configuration */
  configOverride?: Partial<TableConfig>;
  /** Calm empty title when no rows after filters */
  emptyTitle?: string;
  /** Calm empty description */
  emptyDescription?: string;
  /** Error message — shows error state with retry when set */
  errorMessage?: string;
  /** Apply tabular-nums to all body cells (money-friendly default) */
  tabularNums?: boolean;
  /**
   * Cell density. Default `dense` matches MASTER Density 8/10 (dashboard).
   * Use `comfortable` for sparse admin lists.
   */
  density?: TableDensity;
  /** Sticky column headers while body scrolls (default true) */
  stickyHeaders?: boolean;
}

export function EnhancedTable<T = unknown>({
  className,
  tableId,
  enhanced = true,
  zebraStriped = true,
  showToolbar = true,
  onRefresh,
  onExport,
  onFilter,
  onSort,
  configOverride,
  emptyTitle = 'No data yet',
  emptyDescription = 'There is nothing to show here. Try adjusting filters or refresh.',
  errorMessage,
  tabularNums = true,
  density = 'dense',
  stickyHeaders = true,
  loading,
  ...tableProps
}: EnhancedTableProps<T>) {
  const { token } = useTheme();
  const tableService = useTableService(tableId);
  const [filterVisible, setFilterVisible] = useState(false);
  const [sortVisible, setSortVisible] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filterValue, setFilterValue] = useState<string>('all');
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const tableConfig = useMergedConfig(tableService.config, configOverride);
  const hasTableConfig = !!tableId && !!tableConfig;

  const effectiveConfig = useMemo(() => {
    if (!hasTableConfig) return null;
    return tableConfig;
  }, [hasTableConfig, tableConfig]);

  useEffect(() => {
    if (tableId && !hasTableConfig) {
      const defaultConfig: TableConfig = {
        id: tableId,
        name: tableId,
        features: {
          search: true,
          filter: true,
          sort: true,
          pagination: true,
          export: !!onExport,
          refresh: !!onRefresh,
          toolbar: showToolbar,
          enhanced,
          zebraStriped,
          hoverEffects: true,
          stickyHeaders,
          rowSelection: false,
          bulkActions: false,
          columnResize: false,
          columnReorder: false,
          columnVisibility: false,
        },
        defaultPageSize: 20,
        pageSizeOptions: ['10', '20', '50', '100'],
        callbacks: {
          onRefresh,
          onExport,
          onFilter,
          onSort,
        },
      };
      tableService.registerTable(defaultConfig);
    }
  }, [
    tableId,
    tableService,
    onRefresh,
    onExport,
    onFilter,
    onSort,
    showToolbar,
    enhanced,
    zebraStriped,
    stickyHeaders,
    hasTableConfig,
  ]);

  const isFeatureEnabled = (feature: keyof TableFeatures) => {
    if (effectiveConfig) {
      return effectiveConfig.features?.[feature] ?? false;
    }
    // Instrument desks pass showToolbar={false} / pagination={false} —
    // do not force SaaS search chrome when the host opted out.
    switch (feature) {
      case 'search':
        return showToolbar;
      case 'filter':
        return showToolbar;
      case 'sort':
        return showToolbar;
      case 'pagination':
        // Explicit false from host wins; otherwise default on for admin lists
        return tableProps.pagination === false ? false : true;
      case 'export':
        return !!onExport;
      case 'refresh':
        return !!onRefresh;
      case 'toolbar':
        return showToolbar;
      case 'enhanced':
        return enhanced;
      case 'zebraStriped':
        return zebraStriped;
      case 'hoverEffects':
        return true;
      case 'stickyHeaders':
        return stickyHeaders;
      default:
        return false;
    }
  };

  const sortOptions = React.useMemo(() => {
    const columns = tableProps.columns || [];
    return columns
      .filter(
        (col: unknown) =>
          col && typeof col === 'object' && 'dataIndex' in col && 'title' in col
      )
      .map((col: unknown) => ({
        label: (col as { title: string }).title,
        value: (col as { dataIndex: string }).dataIndex,
      }));
  }, [tableProps.columns]);

  const processedData = React.useMemo(() => {
    let data = (tableProps.dataSource || []) as T[];

    if (searchText) {
      data = data.filter((item: unknown) => {
        return Object.values(item as Record<string, unknown>).some((value) =>
          String(value).toLowerCase().includes(searchText.toLowerCase())
        );
      });
    }

    if (filterValue && filterValue !== 'all') {
      data = data.filter((item: unknown) => {
        const itemRecord = item as Record<string, unknown>;
        if (filterValue === 'active') {
          return (
            itemRecord.status === 'active' ||
            itemRecord.status === 'healthy' ||
            itemRecord.status === 'running'
          );
        }
        if (filterValue === 'inactive') {
          return (
            itemRecord.status === 'inactive' ||
            itemRecord.status === 'error' ||
            itemRecord.status === 'stopped'
          );
        }
        return true;
      });
    }

    if (sortField) {
      data = [...data].sort((a: unknown, b: unknown) => {
        const aRecord = a as Record<string, unknown>;
        const bRecord = b as Record<string, unknown>;
        const aValue = aRecord[sortField];
        const bValue = bRecord[sortField];

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortDirection === 'asc'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        }

        return 0;
      });
    }

    return data;
  }, [tableProps.dataSource, searchText, filterValue, sortField, sortDirection]);

  const clearAllFilters = () => {
    setSearchText('');
    setFilterValue('all');
    setSortField('');
    setSortDirection('asc');
  };

  const hasActiveFilters = Boolean(searchText || filterValue !== 'all' || sortField);

  const emptyLocale = useMemo(() => {
    const body = errorMessage ? (
      <FeedbackState
        variant="error"
        compact
        icon={<WarningOutlined />}
        title="Couldn’t load data"
        description={errorMessage}
        actions={
          onRefresh
            ? [
                {
                  label: 'Retry',
                  onClick: onRefresh,
                  tone: 'primary',
                  icon: <ReloadOutlined />,
                },
              ]
            : []
        }
      />
    ) : (
      <FeedbackState
        variant="empty"
        compact
        icon={<InboxOutlined />}
        title={emptyTitle}
        description={
          hasActiveFilters
            ? 'No rows match your search or filters. Clear filters or try a different query.'
            : emptyDescription
        }
        actions={[
          ...(hasActiveFilters
            ? [
                {
                  label: 'Clear filters',
                  onClick: clearAllFilters,
                  tone: 'secondary' as const,
                },
              ]
            : []),
          ...(onRefresh
            ? [
                {
                  label: 'Refresh',
                  onClick: onRefresh,
                  tone: (hasActiveFilters ? 'secondary' : 'primary') as 'primary' | 'secondary',
                  icon: <ReloadOutlined />,
                },
              ]
            : []),
        ]}
      />
    );

    return <div className={styles.emptyCell}>{body}</div>;
    // clearAllFilters is stable enough for locale render; re-run when filter inputs change
  }, [
    errorMessage,
    emptyTitle,
    emptyDescription,
    hasActiveFilters,
    onRefresh,
    searchText,
    filterValue,
    sortField,
  ]);

  const toolbarItems = [
    {
      key: 'filter',
      icon: <FilterOutlined />,
      label: filterVisible ? 'Filter (Active)' : 'Filter',
      onClick: () => setFilterVisible(!filterVisible),
    },
    {
      key: 'sort',
      icon: <SortAscendingOutlined />,
      label: sortVisible ? 'Sort (Active)' : 'Sort',
      onClick: () => setSortVisible(!sortVisible),
    },
    {
      key: 'clear',
      icon: <ReloadOutlined />,
      label: 'Clear All',
      onClick: clearAllFilters,
      disabled: !hasActiveFilters,
    },
    {
      key: 'refresh',
      icon: <ReloadOutlined />,
      label: 'Refresh',
      onClick: onRefresh,
      disabled: !onRefresh,
    },
    {
      key: 'export',
      icon: <DownloadOutlined />,
      label: 'Export',
      onClick: onExport,
      disabled: !onExport,
    },
  ];

  const useSticky = isFeatureEnabled('stickyHeaders');

  const tableClass = [
    styles.table,
    isFeatureEnabled('zebraStriped') ? styles.tableZebra : '',
    density === 'comfortable' ? styles.tableComfortable : '',
    useSticky ? styles.stickyHeader : '',
    tabularNums ? styles.tabular : '',
    className || '',
  ]
    .filter(Boolean)
    .join(' ');

  // Prefer consumer sticky; otherwise enable ant sticky when feature on
  const stickyProp =
    tableProps.sticky !== undefined
      ? tableProps.sticky
      : useSticky
        ? { offsetHeader: 0 }
        : false;

  return (
    <div className={styles.root}>
      {isFeatureEnabled('toolbar') && (
        <div className={styles.toolbar} role="toolbar" aria-label="Table tools">
          <div className={styles.toolbarLeft}>
            {isFeatureEnabled('search') && (
              <Input
                placeholder="Search table…"
                size="middle"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                prefix={<SearchOutlined style={{ color: token.colorTextSecondary }} />}
                className={styles.search}
                allowClear
                aria-label="Search table"
              />
            )}

            {isFeatureEnabled('filter') && filterVisible && (
              <Space>
                <Select
                  size="middle"
                  placeholder="Filter by"
                  value={filterValue}
                  onChange={(value) => setFilterValue(value)}
                  style={{ width: 120 }}
                  aria-label="Filter rows"
                  options={[
                    { label: 'All', value: 'all' },
                    { label: 'Active', value: 'active' },
                    { label: 'Inactive', value: 'inactive' },
                  ]}
                />
              </Space>
            )}

            {isFeatureEnabled('sort') && sortVisible && (
              <Space>
                <Select
                  size="middle"
                  placeholder="Sort by"
                  value={sortField || undefined}
                  onChange={(value) => setSortField(value)}
                  style={{ width: 140 }}
                  aria-label="Sort field"
                  options={sortOptions}
                  allowClear
                />
                <Tooltip title="Sort ascending">
                  <Button
                    type="text"
                    icon={<SortAscendingOutlined />}
                    className={`${styles.toolBtn} ${sortDirection === 'asc' ? styles.toolBtnActive : ''}`}
                    onClick={() => setSortDirection('asc')}
                    aria-label="Sort ascending"
                    aria-pressed={sortDirection === 'asc'}
                  />
                </Tooltip>
                <Tooltip title="Sort descending">
                  <Button
                    type="text"
                    icon={<SortDescendingOutlined />}
                    className={`${styles.toolBtn} ${sortDirection === 'desc' ? styles.toolBtnActive : ''}`}
                    onClick={() => setSortDirection('desc')}
                    aria-label="Sort descending"
                    aria-pressed={sortDirection === 'desc'}
                  />
                </Tooltip>
              </Space>
            )}
          </div>

          <div className={styles.toolbarRight}>
            {isFeatureEnabled('refresh') && onRefresh && (
              <Tooltip title="Refresh data">
                <Button
                  type="text"
                  icon={<ReloadOutlined />}
                  className={styles.toolBtn}
                  onClick={onRefresh}
                  aria-label="Refresh data"
                />
              </Tooltip>
            )}

            {isFeatureEnabled('export') && onExport && (
              <Tooltip title="Export data">
                <Button
                  type="text"
                  icon={<DownloadOutlined />}
                  className={styles.toolBtn}
                  onClick={onExport}
                  aria-label="Export data"
                />
              </Tooltip>
            )}

            <Tooltip title="Table settings">
              <Dropdown
                menu={{
                  items: toolbarItems.map(({ key, icon, label, disabled }) => ({
                    key,
                    icon,
                    label,
                    disabled,
                  })),
                  onClick: ({ key }) => {
                    const item = toolbarItems.find((i) => i.key === key);
                    if (item?.onClick) item.onClick();
                  },
                }}
                trigger={['click']}
                placement="bottomRight"
              >
                <Button
                  type="text"
                  icon={<SettingOutlined />}
                  className={`${styles.toolBtn} ${filterVisible || sortVisible ? styles.toolBtnActive : ''}`}
                  aria-label="Table settings"
                  aria-haspopup="menu"
                />
              </Dropdown>
            </Tooltip>
          </div>
        </div>
      )}

      <div className={styles.scrollX}>
        <Table<T>
          {...tableProps}
          className={tableClass}
          dataSource={errorMessage ? [] : processedData}
          loading={loading}
          size="small"
          bordered={false}
          sticky={stickyProp}
          scroll={{ x: 'max-content', ...tableProps.scroll }}
          locale={{
            emptyText: emptyLocale,
            ...tableProps.locale,
          }}
          pagination={
            isFeatureEnabled('pagination')
              ? {
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total, range) => `${range[0]}–${range[1]} of ${total}`,
                  pageSizeOptions: effectiveConfig?.pageSizeOptions || ['10', '20', '50', '100'],
                  defaultPageSize: effectiveConfig?.defaultPageSize || 20,
                  ...tableProps.pagination,
                }
              : false
          }
        />
      </div>
    </div>
  );
}

export default EnhancedTable;
